import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import { api, onUnauthorized } from "../lib/api";
import {
  clearSession,
  getProfile,
  getValidToken,
  millisUntilExpiry,
  onSessionChangedElsewhere,
  saveSession,
} from "../lib/session";
import type { SignOutReason } from "../lib/session";
import { realtime } from "../lib/ws";
import type { AdminProfile, AdminRole, PermissionAction } from "../types/api";

const ROLE_RANK: Record<AdminRole, number> = {
  support: 1,
  admin: 2,
  superadmin: 3,
};

/** Warn the operator this long before the token dies. */
const EXPIRY_WARNING_MS = 5 * 60 * 1000;

interface AuthValue {
  admin: AdminProfile | null;
  signIn: (username: string, password: string) => Promise<number>;
  signOut: (reason?: SignOutReason) => void;
  /** True when the signed-in admin holds `role` or higher. */
  can: (role: AdminRole) => boolean;
  /** Permission check against the grid the API sent with the profile. */
  allowed: (module: string, action?: PermissionAction) => boolean;
  /** Milliseconds left on the session, or null when it cannot be determined. */
  sessionExpiresIn: number | null;
  expiringSoon: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Seeded from storage so a reload does not flash the sign-in screen. If the
  // stored token has already expired, `getValidToken` clears it and we start out.
  const [admin, setAdmin] = useState<AdminProfile | null>(() =>
    getValidToken() ? getProfile() : null,
  );
  const [expiresIn, setExpiresIn] = useState<number | null>(null);
  const expiryTimer = useRef<number | null>(null);

  const signOut = useCallback((reason: SignOutReason = "manual") => {
    // Tell the server first so the session row is closed, not just forgotten
    // here. Fire-and-forget: a failed call must not strand the operator in a
    // console they have already left.
    if (reason === "manual" && getValidToken()) {
      api.signOut().catch(() => undefined);
    }
    clearSession(reason);
    setAdmin(null);
    setExpiresIn(null);
    realtime.disconnect();
  }, []);

  /** Resolves with how many sessions this sign-in ended elsewhere. */
  const signIn = useCallback(async (username: string, password: string) => {
    const result = await api.signIn(username, password);
    saveSession(result.token, result.admin);
    setAdmin(result.admin);
    realtime.connect(result.token);
    return result.displacedSessions ?? 0;
  }, []);

  // A 401 from anywhere means the server refused our token. The code says why,
  // and the sign-in screen repeats it — "you were idle" and "an administrator
  // ended your session" are different things to be told.
  useEffect(
    () =>
      onUnauthorized((code) => {
        const reason: SignOutReason =
          code === "SESSION_IDLE_TIMEOUT"
            ? "idle"
            : code === "SESSION_EXPIRED"
              ? "expired"
              : code === "SESSION_SUPERSEDED"
                ? "superseded"
                : "rejected";
        signOut(reason);
      }),
    [signOut],
  );

  // Signing out in one tab signs out the rest.
  useEffect(
    () =>
      onSessionChangedElsewhere((hasToken) => {
        if (!hasToken) {
          setAdmin(null);
          realtime.disconnect();
        } else {
          setAdmin(getProfile());
        }
      }),
    [],
  );

  /**
   * Keeps this console's own permissions current.
   *
   * A superadmin revoking a permission has to reach the operator who is using
   * it. Without this, the console would keep offering a screen the API had
   * already started refusing, and the operator would learn about it from a 403
   * halfway through doing something. The grid is also what the menu is built
   * from, so it disappears as soon as it is taken away.
   *
   * Only the permissions are adopted — the identity comes from the token, and a
   * payload is never allowed to change who you are signed in as.
   */
  useEffect(() => {
    if (!admin) return;
    return realtime.watch(
      "account",
      {},
      (payload) => {
        const profile = payload as AdminProfile;
        if (!profile?.permissions) return;
        setAdmin((current) =>
          current && current.id === profile.id
            ? { ...current, role: profile.role, permissions: profile.permissions }
            : current,
        );
      },
      () => undefined,
    );
  }, [admin?.id]);

  // Track time left and expire the session exactly when the token does.
  useEffect(() => {
    if (expiryTimer.current !== null) {
      clearInterval(expiryTimer.current);
      expiryTimer.current = null;
    }
    if (!admin) return;

    const tick = () => {
      const token = getValidToken();
      if (!token) {
        signOut("expired");
        return;
      }
      const remaining = millisUntilExpiry(token);
      setExpiresIn(remaining);
      if (remaining !== null && remaining <= 0) signOut("expired");
    };

    tick();
    expiryTimer.current = window.setInterval(tick, 15_000);
    return () => {
      if (expiryTimer.current !== null) clearInterval(expiryTimer.current);
      expiryTimer.current = null;
    };
  }, [admin, signOut]);

  // Reconnect the socket with the stored token on a cold load.
  useEffect(() => {
    const token = getValidToken();
    if (token) realtime.connect(token);
    return () => realtime.disconnect();
  }, []);

  const can = useCallback(
    (role: AdminRole) =>
      admin ? ROLE_RANK[admin.role] >= ROLE_RANK[role] : false,
    [admin],
  );

  /**
   * Whether this operator may do something, per the grid the API sent.
   *
   * Used to decide what the console offers — never as the control. Every one of
   * these is checked again on the request, so a screen this hides is a route
   * the API refuses anyway, and a screen it wrongly shows fails honestly rather
   * than doing something it should not.
   *
   * A superadmin is answered `true` without consulting anything: they have no
   * stored grid, by design.
   */
  const allowed = useCallback(
    (module: string, action: PermissionAction = 'view') => {
      if (!admin) return false;
      if (admin.role === 'superadmin') return true;
      return Boolean(admin.permissions?.[module]?.[action]);
    },
    [admin],
  );

  const value = useMemo(
    () => ({
      admin,
      signIn,
      signOut,
      can,
      allowed,
      sessionExpiresIn: expiresIn,
      expiringSoon:
        expiresIn !== null && expiresIn > 0 && expiresIn <= EXPIRY_WARNING_MS,
    }),
    [admin, signIn, signOut, can, allowed, expiresIn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
